import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, retry, switchMap, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import * as ReportActions from './report.actions';
import {
  ReportDataService,
  ReportDataParams,
} from '../../../core/services/gse/reports/report-data.service';
import { GetBackJobReportResponse } from '../../../api/gse/models/get-back-job-report-response';
import { GetEquipmentServiceabilityReportResponse } from '../../../api/gse/models/get-equipment-serviceability-report-response';
import { GetEquipmentTatReportResponse } from '../../../api/gse/models/get-equipment-tat-report-response';
import { GetEquipmentDowntimeReportResponse } from '../../../api/gse/models/get-equipment-downtime-report-response';
import { GetInventorySparePartReportResponse } from '../../../api/gse/models/get-inventory-spare-part-report-response';
import { GetInventoryMaintenanceHistoryReportResponse } from '../../../api/gse/models/get-inventory-maintenance-history-report-response';
import { GetInventoryProcurementReportResponse } from '../../../api/gse/models/get-inventory-procurement-report-response';
import { GetPmComplianceReportResponse } from '../../../api/gse/models/get-pm-compliance-report-response';
import { GetJobCardTatReportResponse } from '../../../api/gse/models/get-job-card-tat-report-response';
import { GetTechnicianEfficiencyReportResponse } from '../../../api/gse/models/get-technician-efficiency-report-response';
import { GetDelegationReportResponse } from '../../../api/administration/models/get-delegation-report-response';
import { GetUserReportResponse } from '../../../api/administration/models/get-user-report-response';
import { ReportDataMapperService } from '../../../core/services/gse/reports/report-data-mapper.service';
import { ReportExcelExportService } from '../../../core/services/gse/reports/report-excel-export.service';
import { ToastService } from '../../../core/services/toast.service';

// Common paginated response interface
export interface PaginatedReportResponse<T> {
  data: T[];
  page?: {
    totalElements?: number;
    [key: string]: any;
  };
}

// Type for all possible loader responses
export type ReportLoaderResponse =
  | GetBackJobReportResponse
  | GetEquipmentServiceabilityReportResponse
  | GetEquipmentTatReportResponse
  | GetEquipmentDowntimeReportResponse
  | GetInventorySparePartReportResponse
  | GetInventoryMaintenanceHistoryReportResponse
  | GetInventoryProcurementReportResponse
  | GetPmComplianceReportResponse
  | GetJobCardTatReportResponse
  | GetTechnicianEfficiencyReportResponse
  | GetDelegationReportResponse
  | GetUserReportResponse
  | PaginatedReportResponse<any>;

@Injectable()
export class ReportEffects {
  private actions$ = inject(Actions);
  private reportDataService = inject(ReportDataService);
  private dataMapperService = inject(ReportDataMapperService);
  private excelExportService = inject(ReportExcelExportService);
  private toastService = inject(ToastService);

  private getDataLoader(
    reportType: string
  ): ((params: ReportDataParams) => Observable<ReportLoaderResponse>) | undefined {
    const loaders: Record<string, (params: ReportDataParams) => Observable<ReportLoaderResponse>> =
      {
        'spare-parts': (params) => this.reportDataService.getSparePartsData(params),
        'equipment-tat': (params) => this.reportDataService.getEquipmentTATData(params),
        'maintenance-history': (params) => this.reportDataService.getMaintenanceHistoryData(params),
        procurement: (params) => this.reportDataService.getProcurementData(params),
        'job-card-summary': (params) => this.reportDataService.getJobCardSummaryData(params),
        'back-job': (params) => this.reportDataService.getBackJobData(params),
        'technician-efficiency': (params) =>
          this.reportDataService.getTechnicianEfficiencyData(params),
        'equipment-serviceability': (params) =>
          this.reportDataService.getEquipmentServiceabilityData(params),
        'equipment-downtime': (params) => this.reportDataService.getEquipmentDowntimeData(params),
        'pm-compliance': (params) => this.reportDataService.getPMComplianceData(params),
        delegation: (params) => this.reportDataService.getDelegationData(params),
        'user-master-list': (params) => this.reportDataService.getUserMasterListData(params),
      };
    return loaders[reportType] as
      | ((params: ReportDataParams) => Observable<ReportLoaderResponse>)
      | undefined;
  }

  loadReportData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.loadReportData),
      switchMap(({ reportType, params }) => {
        const loader = this.getDataLoader(reportType);
        if (!loader) {
          return of(
            ReportActions.loadReportDataFailure({
              error: `No data loader for report type: ${reportType}`,
            })
          );
        }
        return loader(params).pipe(
          retry(2),
          map((response: ReportLoaderResponse) => {
            const { data: respData, page } = response;
            const data = (respData || []).map((item) =>
              this.dataMapperService.mapDataToRow(item, reportType)
            );
            return ReportActions.loadReportDataSuccess({
              data,
              totalRecords: page?.totalElements ?? 0,
            });
          }),
          catchError((error) =>
            of(
              ReportActions.loadReportDataFailure({
                error: error?.error?.message || error?.message || 'Failed to load report data',
              })
            )
          )
        );
      })
    )
  );

  loadReportDataFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportActions.loadReportDataFailure),
        tap(() => {})
      ),
    { dispatch: false }
  );

  exportReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.exportReport),
      switchMap(({ reportType, params }) =>
        this.excelExportService.exportReport(reportType, params).pipe(
          map(() => ReportActions.exportReportSuccess()),
          catchError((error) =>
            of(
              ReportActions.exportReportFailure({
                error: error?.message || 'Failed to export report',
              })
            )
          )
        )
      )
    )
  );

  exportReportSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportActions.exportReportSuccess),
        tap(() => {
          this.toastService.showSuccess('Success', 'Report successfully exported.');
        })
      ),
    { dispatch: false }
  );

  exportReportFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportActions.exportReportFailure),
        tap(({ error }) => {
          this.toastService.showError('Export Failed', 'Something went wrong, try again later');
        })
      ),
    { dispatch: false }
  );
}
